import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { Context } from 'components/Store';
import { CROPWISE_PROXY_URL } from 'utilities/apiConstants';
import { parseServerError } from 'helpers/errorHelpers';
import { getRequestConfig } from 'screens/Applications&WorkOrders/apiHelpers';

export type OperationType = 'application' | 'work-order';

export type OperationPropertyVO = {
  farmId: string;
  farmName: string;
  farmArchived: boolean;
  fieldId: string;
  fieldName: string;
  fieldGeometry: any;
  fieldArchived: boolean;
  cropZoneId: string;
  cropZoneName: string;
  cropZoneGeometry?: any;
  cropZoneArea?: number;
  appliedArea: number;
  appliedAreaPercent: number;
  cropZoneArchived: boolean;
  cropName: string;
  cropId: string;
};

interface UseOperationPropertiesReturn {
  propertiesData: OperationPropertyVO[];
  isLoading: boolean;
}

const getEndpoint = (type: OperationType, id: string) => {
  const basePath = type === 'application' ? 'applications' : 'work-orders';
  return `${CROPWISE_PROXY_URL}/v1/base/${basePath}/${id}/properties`;
};

const useOperationProperties = (
  operationId: string | null,
  operationType: OperationType
): UseOperationPropertiesReturn => {
  const [{ organization }, dispatch] = useContext(Context) as any;
  const [isLoading, setIsLoading] = useState(true);
  const [propertiesData, setPropertiesData] = useState<OperationPropertyVO[]>(
    []
  );

  useEffect(() => {
    if (!operationId || !organization?.id) {
      setPropertiesData([]);
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const loadProperties = async () => {
      setIsLoading(true);
      try {
        const config = getRequestConfig(organization.id);
        config.signal = abortController.signal;

        const response = await axios.get<OperationPropertyVO[]>(
          getEndpoint(operationType, operationId),
          config
        );
        setPropertiesData(response.data);
      } catch (error) {
        if (!axios.isCancel(error)) {
          parseServerError(dispatch)(error);
        }
        setPropertiesData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProperties();

    // eslint-disable-next-line consistent-return
    return () => {
      abortController.abort();
    };
  }, [operationId, operationType, organization.id, dispatch]);

  return { propertiesData, isLoading };
};

export default useOperationProperties;
